function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { shallowMemoize } from '@sail/utils';
import { NavigationError } from '../errors';
import toTree from '../internal/compiler/toTree';
import groupSeparator from '../internal/groupSeparator';
import memoizeConfig from '../internal/memoizeConfig';
import urlToLocation from '../internal/urlToLocation';
export function transformParams(params) {
    const paramsPathToRegexp = _object_spread({}, params);
    const paramsLocation = {};
    for(const name in paramsPathToRegexp){
        if (paramsPathToRegexp.hasOwnProperty(name)) {
            const separator = name.indexOf(groupSeparator);
            const value = paramsPathToRegexp[name];
            if (separator === -1) {
                if (paramsLocation.hasOwnProperty(name)) {
                    throw new NavigationError(`Argument name collision in route definition for ${name}`);
                }
                paramsLocation[name] = value;
            } else {
                const key = name.substr(0, separator);
                const sub = name.substr(separator + groupSeparator.length);
                const actualObj = paramsLocation[key];
                if (typeof actualObj === 'string' || Array.isArray(actualObj)) {
                    throw new NavigationError(`Argument name collision in route definition ${name} ${key}`);
                }
                const newObj = actualObj || {};
                newObj[sub] = value;
                paramsLocation[key] = newObj;
            }
        }
    }
    return paramsLocation;
}
const createRoutesIndexMap = shallowMemoize((routes)=>{
    return Object.fromEntries(routes.map((route, index)=>[
            route,
            index
        ]));
});
const normalizeParams = (params, route)=>{
    return Object.fromEntries(Object.entries(params).map(([key, value])=>{
        return [
            key.replace(route, ''),
            value
        ];
    }));
};
export default memoizeConfig(function newMatch(config, path) {
    // TODO: this is memoized but it is temporary since it building the tree can have an impact on TTI
    // we will be building the tree at build time and importing it
    const tree = toTree(config.routes);
    const location = urlToLocation(path);
    const parts = location.pathname.slice(1).split('/');
    const length = parts.length;
    // TODO: using the same shape of response as urlToRoutesOld for now. We can move it to an array in the future
    const finds = {};
    function isGlob(key) {
        return key.includes('.*');
    }
    function isArrayGlob(key, finished) {
        return key.endsWith('*') || !finished && key.endsWith('+');
    }
    function isRegexPattern(key) {
        return key.includes('(');
    }
    function isStatic(part) {
        return !!part;
    }
    function handleGlob(key, index, params, node) {
        var _node_p;
        const newKey = key.replace(/(\(\.?\*\)|\.\*)$/, '');
        const newNode = (_node_p = node.p) === null || _node_p === void 0 ? void 0 : _node_p[key];
        newNode && checkNode(index + parts.slice(index).length, _object_spread_props(_object_spread({}, params), {
            [newKey]: decodeURIComponent(parts.slice(index).join('/'))
        }), newNode);
    }
    function handleArrayGlob(key, index, params, node, finished) {
        const remainingParts = parts.slice(index).filter(Boolean).map(decodeURIComponent);
        // If it's a '+' we need to have at least one part
        let i = key.endsWith('*') ? 0 : 1;
        // If it did finish we need to have all parts
        if (finished) {
            i = remainingParts.length - 1;
        }
        for(i; i <= remainingParts.length; i++){
            var _node_p;
            const globParams = i > 0 ? _object_spread_props(_object_spread({}, params), {
                [key.slice(0, -1)]: remainingParts.slice(0, i)
            }) : params;
            const newNode = (_node_p = node.p) === null || _node_p === void 0 ? void 0 : _node_p[key];
            newNode && checkNode(index + i, globParams, newNode);
        }
    }
    function handleRegex(key, index, params, node, part) {
        const nAndR = key.match(/^([^()]+)(\(.*\))$/);
        if (!nAndR) {
            return;
        }
        // TODO: would be cool to avoid this and have the regular expression
        // already encoded somewhere. But for now it should be OK
        const regExp = new RegExp(`^${nAndR[2]}$`);
        if (regExp.test(part)) {
            var _node_p;
            const newNode = (_node_p = node.p) === null || _node_p === void 0 ? void 0 : _node_p[key];
            newNode && checkNode(index + 1, _object_spread_props(_object_spread({}, params), {
                [nAndR[1]]: decodeURIComponent(part)
            }), newNode);
        }
    }
    function sortFinds() {
        const entries = Object.entries(finds);
        const routesIndexMap = createRoutesIndexMap(tree.l);
        entries.sort((a, b)=>routesIndexMap[a[0]] - routesIndexMap[b[0]]);
        return Object.fromEntries(entries);
    }
    function checkNode(index, params, node) {
        var _node_s;
        const part = parts[index];
        const finished = index >= length;
        // Special case for the '/' route
        if (finished || parts[index] === '' && index === length - 1) {
            var _node_n;
            (_node_n = node.n) === null || _node_n === void 0 ? void 0 : _node_n.forEach((routeName)=>{
                const routeConfig = config.routes[routeName];
                const route = {
                    name: routeName,
                    params: transformParams(normalizeParams(params, routeName)),
                    search: location.search,
                    searchSafe: routeConfig && typeof routeConfig !== 'string' ? routeConfig[1](location.search) : {},
                    hash: location.hash
                };
                // A route can match more than once, we keep the first match.
                if (!finds[routeName]) {
                    Object.assign(finds, {
                        [routeName]: route
                    });
                }
            });
        }
        if (!finished && part && (node === null || node === void 0 ? void 0 : (_node_s = node.s) === null || _node_s === void 0 ? void 0 : _node_s[part.toLowerCase()])) {
            checkNode(index + 1, params, node.s[part.toLowerCase()]);
        }
        for(const key in node.p){
            if (isGlob(key)) {
                handleGlob(key, index, params, node);
            } else if (isArrayGlob(key, finished)) {
                handleArrayGlob(key, index, params, node, finished);
            } else if (!finished && isRegexPattern(key)) {
                handleRegex(key, index, params, node, part);
            } else if (!finished && isStatic(part)) {
                checkNode(index + 1, _object_spread_props(_object_spread({}, params), {
                    [key]: decodeURIComponent(part)
                }), node.p[key]);
            }
        }
    }
    checkNode(0, {}, tree);
    return sortFinds();
});

//# sourceMappingURL=urlToRoutes.js.map